import axios from 'axios';

// Use relative URLs for same-origin requests (no CORS)
// In production, frontend and backend share the same origin (miccheckmillionaire.com)
const BACKEND_URL = '';

// Request interceptor: Add Authorization header if token exists
// Handles both regular user tokens and admin tokens
axios.interceptors.request.use(
  (config) => {
    // Skip if Authorization header already set (admin routes set their own)
    if (config.headers.Authorization) {
      return config;
    }
    
    // Check if this is an admin API call
    const isAdminRoute = config.url?.includes('/api/admin/');
    
    // Use appropriate token based on route type
    const token = isAdminRoute 
      ? localStorage.getItem('admin_access_token')
      : localStorage.getItem('access_token');
      
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors with refresh attempt
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Check if this is an admin route - admin routes handle their own auth
    const isAdminRoute = originalRequest?.url?.includes('/api/admin/');
    
    // For admin routes, don't try to refresh - just reject the error
    // Admin components handle their own 401 errors
    if (isAdminRoute) {
      return Promise.reject(error);
    }

    // If 401 and we haven't tried refreshing yet (NON-ADMIN only)
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        const refreshResponse = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // Send cookies if refresh token is in cookie
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          const newToken = data.token || data.access_token;

          if (newToken) {
            localStorage.setItem('access_token', newToken);
            axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }

        // Refresh failed, clear token and redirect to login
        throw new Error('Token refresh failed');
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Clear token and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        
        // Only redirect if not already on login page and not on admin pages
        const isAdminPage = window.location.pathname.startsWith('/admin');
        const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/signup';
        
        if (!isLoginPage && !isAdminPage) {
          window.location.href = '/login';
        } else if (isAdminPage && window.location.pathname !== '/admin/login') {
          // For admin pages, redirect to admin login
          localStorage.removeItem('admin_access_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
